<template>
  <div class="document-container">
    <div class="document">
      <div class="header">
        <es-header>
          <template #header>
            <div class="header-title">{{title}}</div>
          </template>
          <template #actions>
            <ul>
              <li class="p-narrow no-border">
                <div class="list-content">
                  <router-link
                    :to="{path: '/personId/reports'}"
                    title="Return to reports list"
                    ref="returnLink"
                  >
                    Return to reports list
                  </router-link>
                </div>
              </li>
            </ul>
          </template>
        </es-header>
      </div>
      <div class="content">
        <es-loader v-if="initializing"></es-loader>
        <div class="wrapper" v-else>
          <!-- Person Type, Batch ID and Process Date Range -->
          <div class="columns is-3-desktop multi-dropdown-columns">
            <div class="column is-3">
              <es-select
                :label="`Person Type :`"
                :multi="!contextMode"
                :clearOut="true"
                :preSelected="personTypeCodes"
                :setValues="activePersonTypes"
                v-on:update:values="personTypesChanged($event)"
                ref="selPersonTypes"
                :valueTplFn="(result) => result.name"
                keyValue="code"
                :error-msg="msgPersonType"
                :has-error="hasPersonTypeError"
              />
            </div>

            <div class="column is-3">
              <div class="field">
                <label
                  class="label has-text-weight-bold"
                  for="formIdentifierControl"
                >
                  Identifier :
                </label>
                <div class="control">
                  <input
                      id="formIdentifierControl"
                      class="input"
                      type="text"
                      v-model="id"
                      :maxlength="identifierMaxLength"
                      @input.prevent="idChanged($event.target.value)">
                </div>
              </div>
            </div>

            <div class="column is-3">
              <es-select
                :label="`Identifier Type:`"
                :clearOut="true"
                :preSelected="idType"
                :setValues="idTypes"
                ref="selIdentifierType"
                v-on:update:values="idTypeChanged($event)"
                :valueTplFn="(result) => result.description"
                keyValue="code"
                :error-msg="msgIdType"
                :has-error="hasIdTypeError"
              />
            </div>

            <div class="column is-3">
              <es-select
                :label="`${labels.sourceCode} :`"
                valueTpl="${description}"
                :setValues="sources"
                :multi="true"
                keyValue="code"
                ref="selSourceCode"
                v-on:update:values="sourceCode = $event.map(s => s.code)"
                :clearOut="true"
                :preSelected="sourceCode"
              />
            </div>
          </div>
          <!-- District, School -->
          <div class="columns is-3-desktop multi-dropdown-columns">
            <div class="column is-3">
              <div class="field">
                <label
                  class="label has-text-weight-bold"
                  for="formLastNameControl"
                >
                  {{ labels.lastName }} :
                </label>
                <div class="control">
                  <input
                      id="formLastNameControl"
                      class="input"
                      type="text"
                      v-model="lastName"
                      maxlength="60">
                </div>
              </div>
            </div>

            <div class="column is-3">
              <es-select
                :label="`${labels.district} :`"
                :multi="true"
                :search="true"
                api="districts"
                :apiQueryParams="districtsApiQueryParams"
                v-on:update:values="districtsChanged($event)"
                :preSelected="districtCodes"
                ref="selDistricts"
                :valueTplFn="(result) => `${result.location.name} (${result.code})`"
                searchMinLength="2"
                keyValue="code"
              />
            </div>

            <div class="column is-3">
              <es-select
                :label="`${labels.school} :`"
                :multi="true"
                :search="true"
                api="schools"
                :apiQueryParams="getDistrictParams"
                v-on:update:values="schoolsChanged($event)"
                ref="selSchools"
                :preSelected="schoolCodesPromises"
                :valueTplFn="(result) => `${result.location.name} (${result.code})`"
                :subValueTplFn="(result) =>
                  `${result.districtLocation.name} (<i>${result.districtCode }</i>)`"
                searchMinLength="2"
                :key-value="['districtCode', 'code']"
              />
            </div>

          </div>
          <div class="es-actions">
            <es-form-actions>
              <template #form-actions>
                <ul>
                  <li class="reset">
                    <div class="list-content">
                      <a @click.prevent="reset"
                          @keypress.prevent="reset"
                          tabindex="0"
                          role="button">
                        Reset
                      </a>
                    </div>
                  </li>
                  <li>
                    <div class="list-content">
                      <button
                        class="button is-primary"
                        :disabled="loading || initializing"
                        @click="search"
                        >
                          Customize
                      </button>
                    </div>
                  </li>
                </ul>
              </template>
            </es-form-actions>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// components
import EsHeader from '@/components/EsHeader.vue';
import EsSelect from '@/components/EsSelect.vue';
import EsFormActions from '@/components/EsFormActions.vue';
import EsLoader from '@/components/EsLoader.vue';

// mixins
import NotificationManager from '@/mixins/NotificationManager';
import FieldLabels from '@/mixins/FieldLabels';

// store
import { mapGetters, mapActions } from 'vuex';


// utils
import axios from 'axios';

export default {
  model: {
    prop: 'searchCriteria',
    event: 'searchCriteriaChanged',
  },
  components: {
    EsHeader,
    EsSelect,
    EsFormActions,
    EsLoader,
  },
  mixins: [NotificationManager, FieldLabels],
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    defaultPersonType: {
      type: String,
      default: '',
    },
    activePersonTypes: {
      type: Array,
      default: () => [],
    },
    searchCriteria: {
      type: Object,
      default: () => ({}),
    },
    title: {
      type: String,
      default: '',
    },
  },
  async created() {
    await this.loadSources();

    if (this.searchCriteria) {
      this.id = this.searchCriteria.id;
      this.idType = this.searchCriteria.idType;
      this.lastName = this.searchCriteria.lastName;
      this.districtCodes = this.searchCriteria.districtCodes;
      this.schoolKeys = this.searchCriteria.schoolKeys;
      this.personTypeCodes = this.searchCriteria.personTypeCodes;
      this.sourceCode = this.searchCriteria.sourceCode;
    } else if (this.contextMode) {
      this.personTypeCodes = this.defaultPersonType
        && this.activePersonTypes.some(personType => personType.code === this.defaultPersonType)
        ? [this.defaultPersonType] : [this.activePersonTypes[0].code];
    }

    this.initializing = false;
  },
  data() {
    return {
      id: '',
      messages: [],
      lastName: '',
      idType: ['S'],
      hasPersonTypeError: false,
      hasIdTypeError: false,
      msgPersonType: '',
      msgIdType: '',
      districtCodes: [],
      schoolKeys: [],
      personTypeCodes: [],
      sourceCode: [],
      initializing: true,
      idTypes: [
        {
          code: 'S',
          description: 'State ID',
        },
        {
          code: 'L',
          description: 'Local ID',
        },
      ],
    };
  },
  methods: {
    ...mapActions('lookups', [
      'loadSources',
    ]),
    search() {
      this.checkForm();
      if (!this.hasPersonTypeError && !this.hasIdTypeError) {
        this.searchCriteriaChanged();
        this.$emit('search');
        this.hideNotification();
      } else {
        this.showNotification('danger', ['There were some errors. Please fix the errors highlighted in the fields below.']);
      }
    },
    personTypesChanged(personTypes) {
      this.personTypeCodes = personTypes.map(pt => pt.code);
      this.validateField('personType');
    },
    districtsChanged(districts) {
      this.districtCodes = districts.map(d => d.code);
      this.handleSpecificLocation();
    },
    schoolsChanged(schools) {
      this.schoolKeys = schools.map(s => `${s.districtCode}-${s.code}`);
      this.handleSpecificLocation();
    },
    handleSpecificLocation() {
      const districts = this.$refs.selDistricts.values;
      const schools = this.$refs.selSchools.values;
      const multiple = {
        code: 'Multiple',
        name: 'Multiple',
      };
      let specificLocation = null;

      if (districts && districts.length > 1) {
        specificLocation = {
          district: multiple,
        };
      } else if (districts && districts.length === 1) {
        const district = districts[0];
        specificLocation = {
          district: {
            code: district.code,
            name: district.location.name,
          },
        };
      }

      if (schools && schools.length === 1) {
        const school = schools[0];

        if (schools.length === 1) {
          specificLocation = {
            ...specificLocation,
            school: {
              code: school.code,
              districtCode: school.districtCode,
              name: school.location.name,
            },
          };

          if (!districts || districts.length === 0) {
            specificLocation = {
              ...specificLocation,
              district: {
                code: school.districtCode,
                name: school.districtLocation.name,
              },
            };
          }
        }
      }

      this.$emit('specificLocationChanged', specificLocation);
    },
    idTypeChanged(idTypes) {
      this.idType = idTypes.map(idt => idt.code);
      this.validateField('idType');
    },
    idChanged(value) {
      if (!value) {
        this.hasIdTypeError = false;
        this.msgIdType = null;
      }
    },
    checkForm() {
      this.validateField('personType');
      this.validateField('idType');
    },
    validateField(field) {
      if (field === 'personType' && this.contextMode) {
        if (!this.personTypeCodes || this.personTypeCodes.length === 0) {
          this.hasPersonTypeError = true;
          this.msgPersonType = 'Person Type is required.';
        } else {
          this.hasPersonTypeError = false;
          this.msgPersonType = null;
        }
      }

      if (field === 'idType') {
        if (this.id && (!this.idType || this.idType.length === 0)) {
          this.hasIdTypeError = true;
          this.msgIdType = 'Identifier Type is required.';
        } else {
          this.hasIdTypeError = false;
          this.msgIdType = null;
        }
      }
    },
    reset() {
      this.$refs.selDistricts.clear();
      this.$refs.selIdentifierType.clear();
      this.$refs.selPersonTypes.clear();
      this.$refs.selSchools.clear();
      this.$refs.selSourceCode.clear();

      this.lastName = '';
      this.districtCodes = [];
      this.schoolKeys = [];
      this.sourceCode = [];
      this.id = '';
      this.idType = ['S'];

      // set defaults
      this.$refs.selIdentifierType.pushValues([this.idTypes.find(idt => idt.code === 'S')]);
      if (this.contextMode) {
        this.personTypeCodes = this.defaultPersonType
          && this.activePersonTypes.some(personType => personType.code === this.defaultPersonType)
          ? [this.defaultPersonType] : [this.activePersonTypes[0].code];
        this.$refs.selPersonTypes
          .pushValues(this.activePersonTypes.filter(ap => this.personTypeCodes.includes(ap.code)));
      } else {
        this.personTypeCodes = [];
      }

      this.clearErrors();
      this.searchCriteriaChanged();
      this.$emit('reset');
    },
    searchCriteriaChanged() {
      this.$emit('searchCriteriaChanged', {
        id: this.id,
        districtCodes: this.districtCodes,
        idType: this.idType,
        personTypeCodes: this.personTypeCodes,
        sourceCode: this.sourceCode,
        schoolKeys: this.schoolKeys,
        lastName: this.lastName,
      });
    },
    clearErrors() {
      this.hideNotification();
      this.hasPersonTypeError = false;
      this.hasIdTypeError = false;
      this.msgPersonType = '';
      this.msgIdType = '';
    },
    getIdentifierTypeCode(idType) {
      if (idType === 'S') {
        return 'STATE_STUDENT_ID';
      }
      return 'ALT_STUDENT_ID';
    },
  },
  computed: {
    ...mapGetters('propertiesModule', [
      'properties',
    ]),
    ...mapGetters('lookups', [
      'sources',
    ]),
    ...mapGetters('fieldLabelsModule', [
      'getFieldLengthsByPersonType',
    ]),
    contextMode() {
      const identityControl = this.properties['IDENTITY_CONTROL-LIFETIME_IDS'];
      return JSON.parse(identityControl.toString().toLowerCase()) === false;
    },
    getDistrictParams() {
      return {
        districtCode: this.districtCodes,
        active: false,
      };
    },
    // eslint-disable-next-line vue/no-async-in-computed-properties
    async schoolCodesPromises() {
      if (!this.$route.query.schoolKeys) {
        return [];
      }

      const promises = this.$route.query.schoolKeys.split(',').map((s) => {
        const [districtCode, code] = s.split('-');
        return axios.get(`districts/${districtCode}/schools/${code}`);
      });
      // eslint-disable-next-line vue/no-async-in-computed-properties
      const responses = await Promise.all(promises);
      return responses.map(r => r.data);
    },
    labels() {
      const labelMap = {
        district: 'DISTRICT_CODE',
        school: 'SCHOOL_CODE',
        lastName: 'LAST_NAME',
        sourceCode: 'SOURCE_CODE',
      };
      return Object.entries(labelMap).reduce((acc, [key, value]) => {
        acc[key] = this.getFieldLabel(`PER:${this.defaultPersonType}:ASSIGN:${value}`);
        return acc;
      }, {});
    },
    districtsApiQueryParams() {
      return {
        active: false,
      };
    },
    identifierMaxLength() {
      if (this.personTypeCodes && this.personTypeCodes.length && this.idType) {
        const lenghts = [];
        this.personTypeCodes.forEach((code) => {
          const identifierTypeCode = this.getIdentifierTypeCode(this.idType[0]);
          lenghts.push(this.getFieldLengthsByPersonType(identifierTypeCode, 'ASSIGN', code));
        });
        return Math.max(...lenghts);
      }
      return this.getFieldLengthsByPersonType(
        this.getIdentifierTypeCode(this.idType[0]),
        this.defaultPersonType,
      );
    },
  },
};
</script>

<style lang="sass" scoped>
  @import "@/sass/custom.sass"
  @import "~bulma/sass/utilities/mixins"

  ul li + li
      margin-top: 0

  /deep/ .multi-dropdown-columns.columns
    display: flex
    flex-wrap: wrap
    .column
      flex: 1 0 auto
      width: fit-content
    @media screen and (max-width: 70em)
      .column
        flex: 1 0 100%
        .field
          border-right: none !important
    .field
      .select
        width: 100%
        select
          width: 100%
    .column
      padding-right: 0
      .field
        padding-right: $column-gap
        border-right: 1px dashed #ccc
        @include until($tablet)
          border-right: none
    .column:last-child
      .field
        border-right: none
</style>
